/**
 * "Send the email again" — the way out for a family admin whose address is not verified (#638).
 *
 * `server/auth/services/auth.service.js` refuses an unverified admin with 403, and that check
 * stays exactly as it is (owner decision d-20260926-18). This is what they can DO about it. It is
 * used in two places: the front door, after that 403, and the end of signup, when the first email
 * could not be sent.
 *
 * **The result line never says "sent", and the server's `message` is never shown.** A 2xx from
 * `POST /api/auth/signup/resend-verification` proves only that a link was asked for: an unknown
 * address and a mail that failed both answer "If an account exists with this email, a verification
 * link has been sent." But an address that is ALREADY verified answers "Email is already verified."
 * (`server/services/signup.service.js`), so that body tells a stranger which addresses hold a
 * verified account. This component therefore renders only its own constants below. Rendering the
 * server's `message` here would put that difference on screen.
 *
 * Requests wait 60 s, 429 responses wait the server limiter's 15-minute window, and other
 * failures wait 10 s. ApiError exposes no headers, so Retry-After cannot be read here. Deadlines
 * are per address in sessionStorage, surviving reloads and offer remounts; this is a courtesy,
 * not the server's rate limit. A failed response cannot prove that no request reached the server.
 *
 * **Focus.** The pending/countdown line holds focus lost by the disabled button throughout the
 * request and cooldown. Only focus still parked there (or lost) returns to the enabled button.
 *
 * **Size.** The height is `Button`'s. That is 44 px below 1024 px wide, and 36 px on a wider screen
 * inside the ledger register (`[data-register="ledger"]`), which the front door uses. So on a
 * desktop Login this button is 36 px, like every other button there, "Sign in" included (AD-08).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';

import Button from './Button';
import { ApiError } from '../api/client.js';
import { resendVerification } from '../api/session.js';

export const RESEND_COOLDOWN_SECONDS = 60;
export const RESEND_LIMITED_SECONDS = 15 * 60;
export const RESEND_FAULT_SECONDS = 10;
const DEADLINE_EVENT = 'chorestory:verification-retry';

function deadlineKey(email) {
  return `chorestory.verificationRetry:${email.trim().toLowerCase()}`;
}
function readDeadline(email) {
  try {
    const value = Number(window.sessionStorage.getItem(deadlineKey(email)));
    return Number.isFinite(value) && value > Date.now() ? value : 0;
  } catch { return 0; }
}
function writeDeadline(email, deadline) {
  try { window.sessionStorage.setItem(deadlineKey(email), String(deadline)); }
  catch { /* Same-mount state still enforces the wait when storage is denied. */ }
  // sessionStorage has no same-document storage event. A request can settle after its offer
  // unmounted, so tell any replacement action about the final deadline as well.
  window.dispatchEvent(new CustomEvent(DEADLINE_EVENT, { detail: { key: deadlineKey(email), deadline } }));
}
function secondsLeft(deadline) {
  return Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
}

export const RESEND_REQUESTED =
  'A new verification link has been requested. Please check your inbox, and your spam or junk folder too.';
export const RESEND_LIMITED =
  'That has been asked for a lot just now — please wait 15 minutes before trying again.';
export const RESEND_FAULT = 'We could not reach ChoreStory just now — please wait 10 seconds before trying again.';

const LINES = { requested: RESEND_REQUESTED, limited: RESEND_LIMITED, fault: RESEND_FAULT };

/**
 * @param {object} props
 * @param {string} props.email                   The account's address. The server lowercases it.
 * @param {number} [props.cooldownSeconds=60]    Wait after a successful request before the next.
 */
export default function ResendVerificationAction({ email, cooldownSeconds = RESEND_COOLDOWN_SECONDS }) {
  const [outcome, setOutcome] = useState(null);
  const [sending, setSending] = useState(false);
  const deadlineRef = useRef(readDeadline(email));
  const inFlightRef = useRef(false);
  const [remaining, setRemaining] = useState(() => secondsLeft(deadlineRef.current));
  const buttonRef = useRef(null);
  const countdownRef = useRef(null);
  const parkedRef = useRef(false);
  const cooling = remaining > 0;

  useEffect(() => {
    const update = ({ detail }) => {
      if (detail.key !== deadlineKey(email)) return;
      deadlineRef.current = detail.deadline;
      setRemaining(secondsLeft(detail.deadline));
    };
    window.addEventListener(DEADLINE_EVENT, update);
    return () => window.removeEventListener(DEADLINE_EVENT, update);
  }, [email]);

  useEffect(() => {
    if (remaining <= 0) return undefined;
    const id = setTimeout(() => setRemaining(secondsLeft(deadlineRef.current)), 1000);
    return () => clearTimeout(id);
  }, [remaining]);

  // H1: see "Focus" above. Keyed on the boolean, so the countdown's ticks do not re-run it.
  useEffect(() => {
    const active = document.activeElement;
    const lost = active == null || active === document.body;
    if (cooling || sending) {
      if ((lost || active === buttonRef.current) && countdownRef.current) {
        countdownRef.current.focus();
        parkedRef.current = true;
      }
    } else if (parkedRef.current) {
      parkedRef.current = false;
      if ((lost || active === countdownRef.current) && buttonRef.current) buttonRef.current.focus();
    }
  }, [cooling, sending]);

  const onResend = useCallback(async () => {
    if (inFlightRef.current || secondsLeft(Math.max(deadlineRef.current, readDeadline(email))) > 0) return;
    inFlightRef.current = true;
    const hold = (seconds) => {
      deadlineRef.current = Date.now() + seconds * 1000;
      writeDeadline(email, deadlineRef.current);
      setRemaining(seconds);
    };
    // Reserve before dispatch: even a remount while the response is pending keeps the wait.
    hold(cooldownSeconds);
    setOutcome(null);
    setSending(true);
    try {
      await resendVerification(email);
      setOutcome('requested');
      hold(cooldownSeconds);
    } catch (error) {
      const limited = error instanceof ApiError && error.status === 429;
      setOutcome(limited ? 'limited' : 'fault');
      hold(limited ? RESEND_LIMITED_SECONDS : RESEND_FAULT_SECONDS);
    } finally {
      inFlightRef.current = false;
      setSending(false);
    }
  }, [cooldownSeconds, email]);

  return (
    <div data-component="ResendVerificationAction" style={{ display: 'grid', gap: '0.5rem' }}>
      <Button
        variant="secondary"
        innerRef={buttonRef}
        onClick={onResend}
        disabled={cooling}
        loading={sending}
        busyLabel=""
      >
        Send the email again
      </Button>
      {cooling || sending ? (
        <p ref={countdownRef} tabIndex={-1} style={{ margin: 0 }}>
          {sending ? 'Asking for a verification link…' : `You can ask again in ${remaining} s.`}
        </p>
      ) : null}
      {/* Always in the document, so a screen reader hears the line when it changes. */}
      <p role="status" aria-live="polite" style={{ margin: 0 }}>
        {outcome ? LINES[outcome] : ''}
      </p>
    </div>
  );
}
